// User Types
export interface User {
  id: string;
  email: string;
  name?: string;
  avatar?: string;
  role: UserRole;
  createdAt: string;
  updatedAt: string;
}

export enum UserRole {
  BUYER = 'BUYER',
  SELLER = 'SELLER',
  ADMIN = 'ADMIN'
}

// Product Types
export interface Product {
  id: string;
  title: string;
  description: string;
  price: number;
  category: ProductCategory;
  sector: Sector;
  tags: string[];
  fileUrl: string;
  previewUrl?: string;
  thumbnail?: string;
  downloads: number;
  rating: number;
  reviewCount: number;
  status: ProductStatus;
  createdAt: string;
  updatedAt: string;
  seller: User;
}

export enum ProductCategory {
  POLICY_PACKS = 'POLICY_PACKS',
  RISK_ASSESSMENTS = 'RISK_ASSESSMENTS',
  CARE_PLANS = 'CARE_PLANS',
  HR_RECRUITMENT = 'HR_RECRUITMENT',
  QUALITY_ASSURANCE = 'QUALITY_ASSURANCE',
  INCIDENT_REPORTING = 'INCIDENT_REPORTING',
  TRAINING_INDUCTION = 'TRAINING_INDUCTION',
  FORMS_CHECKLISTS = 'FORMS_CHECKLISTS',
  GOVERNANCE_LEADERSHIP = 'GOVERNANCE_LEADERSHIP',
  FINANCE_BUSINESS = 'FINANCE_BUSINESS'
}

export enum Sector {
  CHILDRENS_HOMES = 'CHILDRENS_HOMES',
  SUPPORTED_ACCOMMODATION = 'SUPPORTED_ACCOMMODATION',
  SUPPORTED_LIVING = 'SUPPORTED_LIVING',
  DOMICILIARY_CARE = 'DOMICILIARY_CARE',
  ADULT_RESIDENTIAL = 'ADULT_RESIDENTIAL',
  FOSTERING_AGENCIES = 'FOSTERING_AGENCIES',
  DAY_CENTRES = 'DAY_CENTRES',
  SPECIALIST_SEN = 'SPECIALIST_SEN',
  MENTAL_HEALTH = 'MENTAL_HEALTH'
}

export enum ProductStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  SUSPENDED = 'SUSPENDED'
}

// Purchase Types
export interface Purchase {
  id: string;
  createdAt: string;
  buyer: User;
  product: Product;
}

// Favorite Types
export interface Favorite {
  id: string;
  createdAt: string;
  user: User;
  product: Product;
}

// Review Types
export interface Review {
  id: string;
  rating: number;
  comment?: string;
  createdAt: string;
  updatedAt: string;
  user: User;
  product: Product;
}

// Filter Types
export interface ProductFilters {
  category?: ProductCategory;
  sector?: Sector;
  minPrice?: number;
  maxPrice?: number;
  search?: string;
  sortBy?: 'newest' | 'oldest' | 'price_low' | 'price_high' | 'rating' | 'downloads';
}

// Dashboard Types
export interface DashboardStats {
  totalPurchases: number;
  totalSales: number;
  totalEarnings: number;
  totalDownloads: number;
  averageRating: number;
}

// Form Types
export interface ProductFormData {
  title: string;
  description: string;
  price: number;
  category: ProductCategory;
  sector: Sector;
  tags: string[];
  file: File;
  previewFile?: File;
}

export interface UserFormData {
  name: string;
  email: string;
  avatar?: File;
}

// API Response Types
export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Navigation Types
export interface NavItem {
  name: string;
  href: string;
  icon?: React.ComponentType<any>;
  current?: boolean;
}

// Constants
export const CATEGORY_LABELS: Record<ProductCategory, string> = {
  [ProductCategory.POLICY_PACKS]: 'Policy Packs',
  [ProductCategory.RISK_ASSESSMENTS]: 'Risk Assessments',
  [ProductCategory.CARE_PLANS]: 'Care Plans & Support Plans',
  [ProductCategory.HR_RECRUITMENT]: 'HR & Recruitment Templates',
  [ProductCategory.QUALITY_ASSURANCE]: 'Quality Assurance & Audits',
  [ProductCategory.INCIDENT_REPORTING]: 'Incident & Reporting Logs',
  [ProductCategory.TRAINING_INDUCTION]: 'Training & Induction Packs',
  [ProductCategory.FORMS_CHECKLISTS]: 'Forms & Checklists',
  [ProductCategory.GOVERNANCE_LEADERSHIP]: 'Governance & Leadership Tools',
  [ProductCategory.FINANCE_BUSINESS]: 'Finance & Business Templates'
};

export const SECTOR_LABELS: Record<Sector, string> = {
  [Sector.CHILDRENS_HOMES]: "Children's Homes (Ofsted)",
  [Sector.SUPPORTED_ACCOMMODATION]: 'Supported Accommodation (16–18)',
  [Sector.SUPPORTED_LIVING]: 'Supported Living Services',
  [Sector.DOMICILIARY_CARE]: 'Domiciliary Care Providers',
  [Sector.ADULT_RESIDENTIAL]: 'Adult Residential & Nursing Homes (CQC)',
  [Sector.FOSTERING_AGENCIES]: 'Fostering Agencies',
  [Sector.DAY_CENTRES]: 'Day Centres & Community Services',
  [Sector.SPECIALIST_SEN]: 'Specialist / SEN Settings',
  [Sector.MENTAL_HEALTH]: 'Mental Health Services'
};

export const REGULATOR_LABELS: Record<Sector, string> = {
  [Sector.CHILDRENS_HOMES]: 'Ofsted',
  [Sector.SUPPORTED_ACCOMMODATION]: 'Ofsted',
  [Sector.SUPPORTED_LIVING]: 'CQC',
  [Sector.DOMICILIARY_CARE]: 'CQC',
  [Sector.ADULT_RESIDENTIAL]: 'CQC',
  [Sector.FOSTERING_AGENCIES]: 'Ofsted',
  [Sector.DAY_CENTRES]: 'CQC',
  [Sector.SPECIALIST_SEN]: 'Ofsted/CQC',
  [Sector.MENTAL_HEALTH]: 'CQC'
};
